import {
  GoabAccordion,
  GoabButton,
  GoabSpacer,
  GoabText,
} from '@abgov/angular-components';
import { Component } from '@angular/core';

interface FaqItem {
  question: string;
  answers: string[];
  bulletPoints?: string[];
}

interface FaqSection {
  title: string;
  items: FaqItem[];
}

@Component({
  selector: 'app-frequently-asked-questions',
  imports: [GoabText, GoabButton, GoabAccordion, GoabSpacer],
  templateUrl: './frequently-asked-questions.html',
  styleUrl: './frequently-asked-questions.scss',
})
export class FrequentlyAskedQuestions {
  readonly sections: FaqSection[] = [
    {
      title: 'About the application',
      items: [
        {
          question:
            'What is the Assisted Living Capacity Reporting and Monitoring application?',
          answers: [
            'It is a digital platform used to track and monitor bed availability across continuing care type A, B, and C licensed spaces in Alberta. It helps to inform operational planning, surge response, and resource allocation.',
          ],
        },
        {
          question: 'Who uses this application?',
          answers: [
            'Facility Reporters submit weekly bed capacity reports, while Program Coordinators oversee the reporting process to ensure accuracy and timeliness. Government leaders and program areas use the capacity reporting dashboard to view data from across the province.',
          ],
        },
      ],
    },
    {
      title: 'Roles, accounts and facility assignments',
      items: [
        {
          question: 'Who is a Facility Reporter?',
          answers: [
            'A Facility Reporter is an individual designated by a facility to submit weekly bed capacity reports on behalf of the facility.',
          ],
        },
        {
          question:
            'Which facilities are assigned to individual facility reporters?',
          answers: [
            'Facility Reporters may be assigned to more than one facility. The designated Owner/Operator and Accommodation contacts are automatically assigned as Facility Reporters for their respective sites.',
            'When requesting an additional user account, please include the name and address of each assigned facility in the template provided above.',
            'To change the facility assignments for an existing reporter, submit the same template as an account update.',
          ],
        },
        {
          question: 'What can Facility Reporters do in the application?',
          answers: [],
          bulletPoints: [
            'Log in using their registered email and password',
            'Record and submit weekly bed capacity',
            'View facility summaries (e.g. report due dates, submission status)',
            'Sort facility name, report due date, and report submitted date',
            'Use the “No changes this week” checkbox to quickly re-submit unchanged reports',
            'Contact application support if needed',
          ],
        },
        {
          question:
            'How does one get added or removed from the Facility reporter role?',
          answers: [
            'Please refer to the section above on account requests and changes. You must complete the template from this help page and email it to the Capacity Reporting team at the email address below. Owner/Operator and Accommodations contacts for each facility are reporters for their facilities and may request additional reporters to assist.',
          ],
        },
        {
          question:
            'What if someone leaves their position and is no longer doing capacity reporting?',
          answers: [
            "Please inform the Capacity Reporting team at the email address below to remove any Facility Reporters who are no longer working in that role. Please share the individual's name, email address, and facility name and address.",
          ],
        },
      ],
    },
    {
      title: 'Reporting requirements and deadlines',
      items: [
        {
          question: 'How often do I need to submit reports?',
          answers: [
            'Reports are due every week for your assigned facility. The reporting period is from Tuesday through to Monday. Reports are due on Tuesday at 4:59 pm. If Tuesday is an Alberta holiday, reports are due the next business day at 4:59 pm.',
          ],
        },
        {
          question:
            'I don’t see any reports to complete when I log in. What should I do?',
          answers: [
            'Congratulations, you are all caught up. New reports to complete are available on Mondays, the last day of the weekly reporting period. Reports are due on Tuesday.',
          ],
        },
        {
          question: 'When can I submit reports?',
          answers: [
            'Reports may be submitted when they are available in the reporting system, generally on Mondays. Reports are due on Tuesday at 4:59 pm. If Tuesday is an Alberta holiday, reports are due the next business day at 4:59 pm.',
          ],
        },
        {
          question: 'What happens if I miss a report deadline?',
          answers: [
            'Program Coordinators may follow up to ensure timely submission.',
          ],
        },
      ],
    },
    {
      title: 'Using the application',
      items: [
        {
          question:
            'If I start an entry, can I leave it and come back to it later?',
          answers: [
            'Yes – When you start an entry, the status of the report is changed from Not Started to In progress. Use the Complete this entry later button. If you close the entry at that stage, it will be saved, and you can pick it up from where you left off.',
          ],
        },
        {
          question:
            'There are numbers already in my reports when I open them. What’s going on?',
          answers: [
            'The numbers from the previous week are carried over. For smaller facilities the numbers may not change week-over-week. If the numbers are the same, please choose the “No changes” checkbox and submit the report. Otherwise enter the numbers for each facility.',
          ],
        },
        {
          question:
            'I don’t see any reports to complete when I log in. What should I do?',
          answers: [
            'Congratulations, you are all caught up. New reports to complete are available on Mondays, the last day of the weekly reporting period. Reports are due on Tuesday.',
          ],
        },
        {
          question:
            'I missed a couple weeks and now I can’t click on some of the report links. How do I fix this?',
          answers: [
            'Reports need to be completed in order, from oldest to newest. Complete the oldest report to unlock the next reporting period. Use the Not Started tab to sort reports from oldest to newest to make this catch up easier. Please request additional accounts for other Facility Reporters to ensure coverage during holidays or illness.',
          ],
        },
        {
          question:
            'If I miss submitting a previous report, can I simply go ahead and submit the most recent one?',
          answers: [
            'No – You will be required to submit all previous reports first. Newer reports will be disabled until all older reports have been submitted.',
          ],
        },
      ],
    },
    {
      title: 'Access and login',
      items: [
        {
          question:
            'If a colleague is not sure how to log in, what should I tell them?',
          answers: [
            'Navigate to www.alacapacity.alberta.ca and use your individual credentials – registered email address and password to access the application. When you first login, please copy and paste your temporary password exactly.',
          ],
        },
        {
          question:
            'What should I tell my colleague if they forget their password or can’t log in?',
          answers: [
            'Use the “Forgot Password?” link on the Sign in page to reset your password. This will email a link to reset your password to the email account on file. If you still cannot log in after a password reset please email the Capacity Reporting team.',
          ],
        },
      ],
    },
  ];

  expandedList: boolean[] = new Array(this.sections.length).fill(false);
  expandedAll = false;

  get accordionStatus(): string {
    return this.expandedAll ? 'Hide all sections' : 'Show all sections';
  }

  toggleAccordion(index: number, open: boolean): void {
    this.expandedList[index] = open;
    this.expandedAll = this.expandedList.every((isOpen) => isOpen);
  }

  onToggleAccordion(): void {
    const nextOpenState = !this.expandedAll;
    this.expandedList = this.expandedList.map(() => nextOpenState);
    this.expandedAll = nextOpenState;
  }
}
